import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotState, setForgotState] = useState(""); // "" | "sent" | "error:<msg>"

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotState("");
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotState("sent");
    } catch (err) {
      setForgotState("error:" + errMsg(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    window.location.href = `/api/auth/google/login`;
  };

  const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

  return (
    <div data-testid="admin-login-page" className="hero-grid flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="card-tactical glow-orange w-full max-w-md p-8">
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="Emisphere Esport" className="mx-auto h-16 w-16 object-contain" />
          <h1 className="mt-4 text-2xl font-extrabold uppercase tracking-tight">Espace Admin</h1>
          <p className="mt-1 text-sm text-slate-400">Réservé à la gestion Emisphere Esport</p>
        </div>
        {user && user.role !== "admin" && (
          <div data-testid="admin-login-member-warning" className="mb-4 rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-xs text-slate-300">
            Vous êtes connecté avec <span className="font-semibold text-white">{user.email}</span> (compte membre).
            Pour gérer le site, connectez-vous ci-dessous avec votre email et mot de passe administrateur.
          </div>
        )}
        <form onSubmit={submit} className="grid gap-4">
          <input type="email" placeholder="Email admin" value={email} data-testid="admin-login-email"
            onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
          <input type="password" placeholder="Mot de passe" value={password} data-testid="admin-login-password"
            onChange={(e) => setPassword(e.target.value)} className={inputCls} required />
          {error && <p data-testid="admin-login-error" className="text-sm font-semibold text-red-400">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-button"
            className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
            <ShieldCheck className="h-5 w-5" /> {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
        <div className="mt-3 text-center">
          <button type="button" onClick={() => { setForgotOpen(!forgotOpen); setForgotState(""); }} data-testid="forgot-password-toggle"
            className="text-xs font-semibold text-slate-400 underline-offset-4 transition-colors hover:text-orange-400 hover:underline">
            Mot de passe oublié ?
          </button>
        </div>
        {forgotOpen && (
          <form onSubmit={submitForgot} className="mt-3 grid gap-3 rounded-lg border border-orange-500/20 bg-orange-500/5 p-4" data-testid="forgot-password-form">
            <p className="text-xs text-slate-400">Recevez un lien de réinitialisation par email (valable 1 h).</p>
            <input type="email" placeholder="Votre email admin" value={forgotEmail} data-testid="forgot-password-email"
              onChange={(e) => setForgotEmail(e.target.value)} className={inputCls} required />
            {forgotState === "sent" && (
              <p className="text-sm font-semibold text-emerald-400" data-testid="forgot-password-sent">
                Si un compte admin existe avec cet email, un lien de réinitialisation vient d'être envoyé.
              </p>
            )}
            {forgotState.startsWith("error:") && (
              <p className="text-sm font-semibold text-red-400" data-testid="forgot-password-error">{forgotState.slice(6)}</p>
            )}
            <button type="submit" data-testid="forgot-password-submit"
              className="flex items-center justify-center gap-2 rounded-lg border border-orange-500/40 px-4 py-2 text-sm font-bold uppercase tracking-wider text-orange-400 transition-all hover:bg-orange-500/10">
              <Mail className="h-4 w-4" /> Envoyer le lien
            </button>
          </form>
        )}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-700" />
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500">ou</span>
          <div className="h-px flex-1 bg-slate-700" />
        </div>
        <button onClick={googleLogin} data-testid="google-login-button"
          className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-600 bg-white px-6 py-3 text-sm font-bold text-slate-800 transition-all hover:scale-[1.02] hover:bg-slate-100 active:scale-[0.98]">
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a7.16 7.16 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continuer avec Google
        </button>
        <p className="mt-3 text-center text-[11px] text-slate-500">
          Le compte Google lié à l'email administrateur obtient automatiquement l'accès gestion.
        </p>
      </div>
    </div>
  );
}
